import apiClient from './apiService';

// CAPA service — Issues only. Remedial actions are tasks (see tasksService) with
// source_type_id=CAPA_ISSUE and source_record_id=<issue_id>.

export type IssueSourceType =
    | 'Audit' | 'Compliance' | 'Incident Reporting'
    | 'Control Test' | 'Risk Assessment' | 'Continuous Monitoring' | 'Manual';

export interface CapaIssue {
    id: number;
    entity_id: number;
    issue_code: string;
    issue_name: string;
    issue_description?: string | null;
    issue_source_type?: IssueSourceType | null;
    source_record_id?: number | null;
    is_trivial: boolean;
    issue_head_user_id: number;
    is_resolved: boolean;
    is_active: number;
    created_by?: number;
    created_on?: string;
    modified_by?: number | null;
    modified_on?: string | null;
    triage_outcome?: 'TRIVIAL' | 'INVESTIGATION' | 'ACTION_ONLY' | null;
    // Server-enriched
    issue_head_name?: string | null;
    action_count?: number;
    open_action_count?: number;
    // Split out of the description blob at serve time; raw ISO (client formats it).
    target_date?: string | null;
    // System-generated code (e.g. "CAP-1") — set on create
    code?: string | null;
}

export interface CapaIssueCreate {
    issue_code: string;
    issue_name: string;
    issue_description?: string;
    issue_source_type?: IssueSourceType;
    source_record_id?: number | null;
    is_trivial?: boolean;
    issue_head_user_id?: number | null;
    is_resolved?: boolean;
}

export type CapaIssueUpdate = Partial<CapaIssueCreate> & { is_active?: number };

export interface CapaListParams {
    q?: string;
    source_type?: IssueSourceType;
    resolved?: boolean;
    limit?: number;
    offset?: number;
}

export type TriageOutcome = 'TRIVIAL' | 'INVESTIGATION' | 'ACTION_ONLY' | null;

export interface CreateActionPayload {
    name: string;
    description?: string;
    due_date?: string | null;
    doer_id?: number | null;
    validator_id?: number | null;
    approver_id?: number | null;
    priority?: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL' | null;
}

// ---------- Insights ----------
export interface CapaCount {
    label: string;
    count: number;
}

export interface CapaRecurrent {
    label: string;
    count: number;
    trend_pct: number;
    rising: boolean;
}

export interface CapaInsights {
    in_investigation: number;
    in_remediation: number;
    pending_feedback: number;
    avg_closure_days: number;
    overdue_open: number;
    by_bucket: CapaCount[];
    by_category: CapaCount[];
    by_source: CapaCount[];
    impact_severity: CapaCount[];
    recurrent: CapaRecurrent[];
}

export const capaInsightsApi = {
    get: async (): Promise<CapaInsights> => {
        const resp = await apiClient.get('/api/v1/cap/insights');
        return resp.data?.data;
    },
};

export const capaService = {
    list: async (params: CapaListParams = {}): Promise<CapaIssue[]> => {
        const resp = await apiClient.get('/api/v1/cap/issues', { params });
        return resp.data?.data ?? [];
    },
    listPaged: async (params: CapaListParams = {}): Promise<{ data: CapaIssue[]; total: number }> => {
        const resp = await apiClient.get('/api/v1/cap/issues', { params });
        const data: CapaIssue[] = resp.data?.data ?? [];
        return { data, total: resp.data?.total ?? data.length };
    },
    get: async (id: number): Promise<CapaIssue> => {
        const resp = await apiClient.get(`/api/v1/cap/issues/${id}`);
        return resp.data?.data;
    },
    create: async (data: CapaIssueCreate): Promise<CapaIssue> => {
        const resp = await apiClient.post('/api/v1/cap/issues', data);
        return resp.data?.data;
    },
    update: async (id: number, data: CapaIssueUpdate): Promise<CapaIssue> => {
        const resp = await apiClient.put(`/api/v1/cap/issues/${id}`, data);
        return resp.data?.data;
    },
    remove: async (id: number): Promise<void> => {
        await apiClient.delete(`/api/v1/cap/issues/${id}`);
    },
    // Triage outcome transitions (T5)
    setTriage: async (id: number, outcome: 'TRIVIAL' | 'ACTION_ONLY' | null): Promise<CapaIssue> => {
        const resp = await apiClient.patch(`/api/v1/cap/issues/${id}/triage`, { outcome });
        return resp.data?.data;
    },
    createAction: async (id: number, data: CreateActionPayload): Promise<{ task_id: number; issue_id: number }> => {
        const resp = await apiClient.post(`/api/v1/cap/issues/${id}/triage/create-action`, data);
        return resp.data?.data;
    },
};
